export type ExtractedHeaderType = {
    modelName: string | null
    modelType: string | null
    modelVersion: string | null
    serviceVersion: string | null
    xRequestId?: string
} | null

export function extractHeadersFromError(error: any): ExtractedHeaderType {
    try {
        let extractedHeaders: any = null

        // Check if the error has an errorResponse (from your authFetch)
        if (error?.errorResponse instanceof Response) {
            // If errorResponse is a Response object, get headers from it
            extractedHeaders = error.errorResponse.headers
            console.log('Found Response headers') // Debug log
        }
        // Check if errorResponse has headers directly
        else if (error?.errorResponse?.headers) {
            extractedHeaders = error.errorResponse.headers
            console.log('Found errorResponse.headers') // Debug log
        }
        // Check if the error itself has headers (direct fetch response)
        else if (error?.headers) {
            extractedHeaders = error.headers
            console.log('Found error.headers') // Debug log
        }
        // Check for nested response structure
        else if (error?.response?.headers) {
            extractedHeaders = error.response.headers
            console.log('Found error.response.headers') // Debug log
        }

        if (extractedHeaders) {
            const getHeader = (key: string) => {
                // Handle Headers object (from fetch Response)
                if (typeof extractedHeaders.get === 'function') {
                    return (
                        extractedHeaders.get(key) ||
                        extractedHeaders.get(key.toLowerCase())
                    )
                }
                // Handle plain object headers
                return (
                    extractedHeaders[key] ||
                    extractedHeaders[key.toLowerCase()] ||
                    extractedHeaders[key.toUpperCase()]
                )
            }

            const modelName = getHeader('x-model-name') || ''
            const modelType = getHeader('x-model-type') || ''
            const modelVersion = getHeader('x-model-version') || ''
            const serviceVersion = getHeader('x-service-version') || ''
            const xRequestId =
                getHeader('x-request-id') || getHeader('X-Request-Id') || ''

            console.log('Extracted headers:', {
                // Debug log
                modelName,
                modelType,
                modelVersion,
                serviceVersion,
                xRequestId,
            })

            if (
                modelName ||
                modelType ||
                modelVersion ||
                serviceVersion ||
                xRequestId
            ) {
                return {
                    modelName,
                    modelType,
                    modelVersion,
                    serviceVersion,
                    xRequestId,
                }
            }
        }
    } catch (e) {
        console.warn('Could not extract headers from error:', e)
    }

    return null
}

export function extractHeadersFromResponse(response: any): ExtractedHeaderType {
    if (!response.headers) {
        return null
    }
    const headers = {
        modelName: response.headers['x-model-name'] || null,
        modelType: response.headers['x-model-type'] || null,
        modelVersion: response.headers['x-model-version'] || null,
        serviceVersion: response.headers['x-service-version'] || null,
    }

    return headers
}

/**
 * Extracts polar plot request_id from an error report (create/recalculate/get-by-id).
 * Prefers X-Request-Id from response header (via extractHeadersFromError), then falls back to body.
 * Using the header first ensures audit-log can be fetched even when the response body is missing or
 * unparseable (e.g. 500 with HTML or failed JSON parse), since headers are captured before body read.
 */
export function getRequestIdFromErrorReport(errorReport: any): string | undefined {
    if (errorReport == null) return undefined
    try {
        const r = errorReport

        // Prefer X-Request-Id from response header (works even when body is unavailable or unparseable)
        const extracted = extractHeadersFromError(r)
        if (extracted?.xRequestId) return String(extracted.xRequestId)

        // Fallback: API body { error, message, request_id } - auth-fetch puts in response.data or errorData
        const data = r?.errorData ?? r?.response?.data
        if (data && typeof data === 'object') {
            const id = data.request_id ?? data.requestId
            if (id != null && id !== '') return String(id)
        }
        if (typeof data === 'string') {
            try {
                const parsed = JSON.parse(data)
                const id = parsed?.request_id ?? parsed?.requestId
                if (id != null && id !== '') return String(id)
            } catch {
                // ignore parse errors
            }
        }

        const topLevel = r.request_id ?? r.requestId
        return topLevel != null && topLevel !== '' ? String(topLevel) : undefined
    } catch {
        return undefined
    }
}

/**
 * Builds a short summary from a polar plot error report so the downloaded JSON
 * allows quick identification of failure reason (http_status, error_type, message, request_id).
 */
export function buildPolarPlotFailureSummary(
    errorReport: any,
    requestId: string | undefined,
): { http_status: number | null; error_type: string; message: string; request_id: string | undefined } {
    const data = errorReport?.response?.data ?? errorReport?.errorData ?? null
    const status = errorReport?.response?.status ?? null
    const errorType =
        (data && typeof data === 'object' && data.error_type != null && data.error_type !== '')
            ? String(data.error_type)
            : 'unknown'
    // Prefer detailed "error" over "message" so summary is consistent and informative
    const message =
        (data && typeof data === 'object' && (data.error ?? data.message)) ||
        errorReport?.error ||
        errorReport?.message ||
        'Unknown error'
    const rid = requestId ?? (data && typeof data === 'object' && data.request_id) ?? errorReport?.request_id
    return {
        http_status: status != null ? Number(status) : null,
        error_type: typeof errorType === 'string' ? errorType : 'unknown',
        message: typeof message === 'string' ? message : String(message),
        request_id: rid != null && rid !== '' ? String(rid) : undefined,
    }
}
